import React, { useState, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { USER_ROLES } from '../../constants';
import styles from './Layout.module.css';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const toggleMenu = useCallback(() => {
    setMenuOpen(prev => !prev);
  }, []);

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const getRoleLabel = () => {
    switch (user?.role) {
      case USER_ROLES.SUPER_ADMIN: return 'Super Admin';
      case USER_ROLES.OWNER: return 'Owner';
      case USER_ROLES.MANAGER: return 'Manager';
      case USER_ROLES.EMPLOYEE: return 'Employee';
      default: return '';
    }
  };

  return (
    <div className={styles.layout}>
      <nav className={styles.navbar}>
        <div className={styles.navContent}>
          <Link to="/dashboard" className={styles.logo} onClick={closeMenu}>
            Turna
          </Link>

          <button
            className={`${styles.hamburger} ${menuOpen ? styles.hamburgerOpen : ''}`}
            onClick={toggleMenu}
            aria-label="Menu"
          >
            <span />
            <span />
            <span />
          </button>

          <div className={`${styles.navLinks} ${menuOpen ? styles.navLinksOpen : ''}`}>
            <Link
              to="/dashboard"
              className={`${styles.navLink} ${isActive('/dashboard') ? styles.navLinkActive : ''}`}
              onClick={closeMenu}
            >
              Dashboard
            </Link>
            <Link
              to="/shifts"
              className={`${styles.navLink} ${isActive('/shifts') ? styles.navLinkActive : ''}`}
              onClick={closeMenu}
            >
              Turni
            </Link>
            <Link
              to="/calendar"
              className={`${styles.navLink} ${isActive('/calendar') ? styles.navLinkActive : ''}`}
              onClick={closeMenu}
            >
              Calendario
            </Link>
            <Link
              to="/requests"
              className={`${styles.navLink} ${isActive('/requests') ? styles.navLinkActive : ''}`}
              onClick={closeMenu}
            >
              Richieste
            </Link>
            <Link
              to="/emergency-reports"
              className={`${styles.navLink} ${isActive('/emergency-reports') ? styles.navLinkActive : ''}`}
              onClick={closeMenu}
            >
              Segnalazioni
            </Link>
            <Link
              to="/loyalty"
              className={`${styles.navLink} ${isActive('/loyalty') ? styles.navLinkActive : ''}`}
              onClick={closeMenu}
            >
              Punti
            </Link>
            {user && user.role <= USER_ROLES.MANAGER && (
              <>
                <Link
                  to="/sites-overview"
                  className={`${styles.navLink} ${isActive('/sites-overview') ? styles.navLinkActive : ''}`}
                  onClick={closeMenu}
                >
                  Stato Sedi
                </Link>
                <Link
                  to="/users"
                  className={`${styles.navLink} ${isActive('/users') ? styles.navLinkActive : ''}`}
                  onClick={closeMenu}
                >
                  Utenti
                </Link>
              </>
            )}
            {user && user.role <= USER_ROLES.OWNER && (
              <>
                <Link
                  to="/sites"
                  className={`${styles.navLink} ${isActive('/sites') ? styles.navLinkActive : ''}`}
                  onClick={closeMenu}
                >
                  Sedi
                </Link>
                <Link
                  to="/prize-management"
                  className={`${styles.navLink} ${isActive('/prize-management') ? styles.navLinkActive : ''}`}
                  onClick={closeMenu}
                >
                  Premi
                </Link>
              </>
            )}
            {user && user.role === USER_ROLES.SUPER_ADMIN && (
              <Link
                to="/companies"
                className={`${styles.navLink} ${isActive('/companies') ? styles.navLinkActive : ''}`}
                onClick={closeMenu}
              >
                Aziende
              </Link>
            )}
          </div>

          <div className={styles.navRight}>
            <div className={styles.userInfo}>
              <span className={styles.userName}>{user?.fullName}</span>
              <span className={styles.userRole}>{getRoleLabel()}</span>
            </div>
            <button onClick={handleLogout} className={styles.logoutButton}>
              Esci
            </button>
          </div>
        </div>
      </nav>

      <main className={styles.main}>{children}</main>
    </div>
  );
};

Layout.displayName = 'Layout';
